import { Link } from 'react-router-dom';
import { LogoLockup } from '@/components/brand/Logo';

/**
 * The site footer, shared by the landing page and every marketing page.
 *
 * ## Root-relative fragments, always
 *
 * `#pricing` means "a section of this page". On an article, a documentation page or the privacy
 * policy there is no such section, so the link silently does nothing — which is exactly the class
 * of dead link this footer exists to avoid. Every section link is `/#pricing`, a real navigation
 * that lands on the landing page and scrolls.
 *
 * ## What is deliberately absent
 *
 * No compliance badges, no certification logos, no "enterprise-grade security" strip. The platform
 * holds no SOC 2 report and no ISO certification, and a footer that implies otherwise is the
 * cheapest possible lie to tell. The standing rule recorded as C10 applies here as much as to the
 * landing page: state what the code does.
 *
 * No social icons either, for the simpler reason that the accounts do not exist.
 *
 * ## Product is what you buy, Resources is what you read
 *
 * "Features" used to sit under Product, but the page it opens is an index of articles — how each part
 * of the platform works, in detail — which is reading material in the same sense the documentation
 * and the monitoring guides are. So the articles, the guides, the documentation, the blog and the
 * questions are one column, and Product is left with the three parts of the platform and the price. Individual
 * playbooks, Advanced Security among them, are part of Automation and are reached through the feature
 * articles, not listed as products.
 */

const PRODUCT = [
  { label: 'Automation', to: '/#automation' },
  { label: 'Monitoring', to: '/#monitoring' },
  { label: 'Backups', to: '/features/backups' },
  { label: 'Pricing', to: '/pricing' },
];

const RESOURCES = [
  { label: 'Documentation', to: '/docs' },
  { label: 'Feature Articles', to: '/features' },
  { label: 'Monitoring Guides', to: '/guides' },
  { label: 'Blog', to: '/blog' },
  { label: 'FAQ', to: '/#faq' },
];

const COMPANY = [
  { label: 'Contact', to: '/contact' },
  { label: 'Privacy Policy', to: '/privacy' },
  { label: 'Terms of Service', to: '/terms' },
];

const LINK_CLASS = 'text-muted-foreground hover:text-foreground text-sm transition-colors';

/**
 * Every link is a router link, including the `/#pricing` ones.
 *
 * React Router does no fragment scrolling of its own — it pushes history state and stops — so these
 * depend on {@see ScrollToTop} to finish the job. That component also covers the case a plain
 * anchor cannot: arriving at `/#features` from another page, where the landing section does not
 * exist yet when the browser looks for it.
 */
function Column({ title, links }: { title: string; links: { label: string; to: string }[] }) {
  return (
    <div>
      <h3 className="text-foreground text-sm font-medium">{title}</h3>
      <ul className="mt-4 space-y-3">
        {links.map((link) => (
          <li key={link.label}>
            {link.to.includes('#') ? (
              // A real browser navigation, not a router link.
              //
              // `<Link to="/#pricing">` was verified not to reach `useLocation` at all: clicking it
              // changed the URL and fired no location update, so nothing scrolled and the visitor
              // stayed in the footer. A plain anchor performs a navigation the browser and the
              // router both agree happened, and ScrollToTop's retry handles the landing section not
              // existing yet when the page mounts.
              <a href={link.to} className={LINK_CLASS}>
                {link.label}
              </a>
            ) : (
              <Link to={link.to} className={LINK_CLASS}>
                {link.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="border-border/60 border-t">
      <div className="mx-auto max-w-6xl px-6 py-14">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1fr] lg:gap-8">
          <div className="lg:pr-8">
            <Link to="/">
              <LogoLockup size={38} subtitle />
            </Link>
            <p className="text-muted-foreground mt-5 max-w-sm text-sm leading-relaxed">
              Automation, monitoring, Auto-Fix and verified backups for Linux servers on any provider.
            </p>
          </div>

          <Column title="Product" links={PRODUCT} />
          <Column title="Resources" links={RESOURCES} />
          <Column title="Company" links={COMPANY} />
        </div>
      </div>

      <div className="border-border/60 border-t">
        <div className="mx-auto max-w-6xl px-6 py-8">
          {/* The year the page was rendered, not a literal that goes stale every January. */}
          <p className="text-muted-foreground/70 text-xs">
            © {new Date().getFullYear()} Techie Gain. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
